import { useTranslation } from 'react-i18next';
import { QueryErrorState } from '../../components/dashboard/QueryErrorState';
import { QueryLoadingBlock } from '../../components/dashboard/QueryLoadingBlock';
import { useQueryRetry } from '../../lib/dashboard/useQueryRetry';
import { useMyProfile } from '../../lib/profile/useMyProfile';
import { ProfileLanguageRow } from './ProfileLanguageRow';
import { ProfileNameForm } from './ProfileNameForm';

type ProfileSectionProps = {
  onSaved: (message: string) => void;
};

/** Personal details — they belong to the signed-in person, not the organisation. */
export function ProfileSection({ onSaved }: ProfileSectionProps) {
  const { t } = useTranslation('settings');
  const profileQuery = useMyProfile();
  const retry = useQueryRetry(profileQuery.refetch);

  return (
    <section aria-labelledby="settings-profile-heading" className="flex w-full max-w-[760px] flex-col gap-2">
      <h2
        id="settings-profile-heading"
        className="m-0 text-[11px] font-bold uppercase tracking-[0.14em]"
        style={{ color: 'var(--gs-muted)' }}
      >
        {t('profile.heading')}
      </h2>
      {profileQuery.isError || retry.isRetrying ? (
        <QueryErrorState
          message={t('profile.loadError')}
          onRetry={retry.retry}
          isRetrying={retry.isRetrying}
        />
      ) : profileQuery.isPending ? (
        <QueryLoadingBlock />
      ) : (
        <div
          className="overflow-hidden rounded-[14px] border"
          style={{ background: 'var(--gs-panel)', borderColor: 'var(--gs-line)' }}
        >
          <ProfileNameForm
            key={profileQuery.data.userId}
            profile={profileQuery.data}
            onSaved={onSaved}
          />
          <ProfileLanguageRow onSaved={onSaved} />
        </div>
      )}
    </section>
  );
}
